import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Zap } from 'lucide-react';
import {
  Callout,
  FilterChips,
  ListGroupLabel,
  WorkspaceButton,
  WorkspaceMenu,
  linkishClass,
  useToast,
} from '@/components/ui/workspace';
import { Spinner } from '@/components/ui/spinner';
import { ApiError } from '@/lib/api/client';
import { discoveryKeys } from '@/lib/api/discovery';
import {
  EVIDENCE_LABELS,
  useEvidenceLabellingQuery,
  useLabelEvidence,
  useSetEvidenceLabel,
  useWithdrawEvidence,
  type AreaFindings,
  type EvidenceLabel,
  type QuotedEvidence,
} from '@/lib/api/discovery-findings';
import { cx } from '@/lib/utils/cn';
import { newIdempotencyKey } from '@/lib/api/engagement-requirements';
import { DocBadge, PersonAvatar } from '../coverage-bits';
import { plural } from '../discovery-logic';
import { SaidRow } from './evidence-bits';
import { EVIDENCE_LABEL_TEXT, withdrawnToast } from './findings-logic';
import type { QuoteSupport } from './process-steps';

/**
 * The Quotes view of an area (the Findings area page, 5 Nov 2026): what
 * people said, every confirmed item with its number and citation, grouped
 * by who said it so an area resting on one voice shows at a glance. A
 * withdrawn item stays in place, struck through, with its reason
 * (Decision 53.3). Each quote names the step and the pain point it
 * supports; items still waiting for review are one line with the way to
 * the review queue.
 *
 * Each quote carries what it describes (T-0285, E25-design §3): how the
 * process runs, volumes and timing, systems and files, problems, or needs.
 * The chips above the list filter by it and show each count, so a zero
 * says what nobody has been asked. The AI reader proposes the label and
 * anyone can change it on the quote; it is a tag and never counts. The
 * engagement lead or a firm admin can ask AI to label the confirmed quotes
 * that have none, across the engagement: the run writes its labels when it
 * ends, and it never touches a quote a person labelled or cleared.
 */

type LabelFilter = 'all' | 'none' | EvidenceLabel;

/** The label on a quote: press it to change it. An AI's label wears the AI tint until a person sets one. */
function LabelMenu({
  item,
  busy,
  onSet,
}: {
  item: QuotedEvidence;
  busy: boolean;
  onSet: (label: EvidenceLabel | null) => void;
}) {
  const label = item.label ?? null;
  return (
    <WorkspaceMenu
      aria-label="What it describes"
      label="What it describes"
      align="start"
      trigger={
        <button
          type="button"
          title={
            label === null
              ? 'Say what this quote describes'
              : item.label_set_by === 'ai'
                ? 'Labelled by AI. Change it if it is wrong.'
                : 'Change what this quote describes'
          }
          className={cx(
            'rounded-chip px-1.5 text-[11px] leading-[18px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600',
            label === null
              ? 'border border-dashed border-ink-200 text-ink-500 hover:text-ink-900'
              : item.label_set_by === 'ai'
                ? 'border border-ai-surface-border bg-ai-surface text-ai-ink'
                : 'bg-paper-100 text-ink-700',
          )}
        >
          {label === null ? 'Add label' : EVIDENCE_LABEL_TEXT[label]}
        </button>
      }
      items={[
        ...EVIDENCE_LABELS.map((l) => ({
          id: l,
          label: EVIDENCE_LABEL_TEXT[l],
          hint: l === label ? 'current' : undefined,
          disabled: busy || l === label,
          onSelect: () => onSet(l),
        })),
        {
          id: 'none',
          label: 'No label',
          separatorBefore: true,
          disabled: busy || label === null,
          onSelect: () => onSet(null),
        },
      ]}
    />
  );
}

interface Group {
  key: string;
  kind: 'person' | 'document' | 'other';
  id: string;
  name: string;
  items: QuotedEvidence[];
}

/** The quotes by source, in the order the area lists its sources; anything else last. */
export function groupBySource(
  said: ReadonlyArray<QuotedEvidence>,
  sources: AreaFindings['sources'],
): Group[] {
  const groups = new Map<string, Group>();
  for (const s of sources) {
    groups.set(s.key, { key: s.key, kind: s.kind, id: s.id, name: s.name, items: [] });
  }
  const other: Group = { key: 'other', kind: 'other', id: '', name: 'Other sources', items: [] };
  for (const e of said) (groups.get(e.source_key ?? '') ?? other).items.push(e);
  return [...groups.values(), other].filter((g) => g.items.length > 0);
}

function SupportTags({ support }: { support: QuoteSupport | undefined }) {
  if (!support || (support.steps.length === 0 && support.pain_points.length === 0)) return null;
  const mark = 'rounded-chip bg-paper-100 px-1.5 font-mono text-[11px] text-ink-700';
  return (
    <>
      {support.steps.map((n) => (
        <span key={`s${n}`} className={mark} title={`Describes step ${n} of the process`}>
          step {n}
        </span>
      ))}
      {support.pain_points.map((n) => (
        <span key={n} className={mark} title={`${n} rests on it`}>
          {n}
        </span>
      ))}
    </>
  );
}

export function QuotesView({
  engagementId,
  areaId,
  said,
  sources,
  waiting,
  source,
  onShowEveryone,
  highlight,
  support,
  onReview,
}: {
  engagementId: string;
  areaId: string;
  said: AreaFindings['said'];
  sources: AreaFindings['sources'];
  waiting: AreaFindings['waiting'];
  /** The source chip that is pressed: only what that source said. */
  source: string | null;
  onShowEveryone: () => void;
  /** An evidence id to bring into view, from the ledger. */
  highlight: string | null;
  support: ReadonlyMap<string, QuoteSupport>;
  /** Open the review queue on this area. */
  onReview: (review: string) => void;
}) {
  const { toast } = useToast();
  const withdraw = useWithdrawEvidence(engagementId);
  const setLabel = useSetEvidenceLabel(engagementId);
  const labelling = useLabelEvidence(engagementId);
  const [labelFilter, setLabelFilter] = React.useState<LabelFilter>('all');
  const [withdrawing, setWithdrawing] = React.useState<string | null>(null);
  const [withdrawError, setWithdrawError] = React.useState<string | null>(null);

  const doWithdraw = (evidenceId: string, number: string | null, reason: string) => {
    setWithdrawError(null);
    withdraw.mutate(
      { id: evidenceId, reason, key: newIdempotencyKey() },
      {
        onSuccess: (r) => {
          setWithdrawing(null);
          toast({ message: withdrawnToast(number, r) });
        },
        onError: (e) =>
          setWithdrawError(e instanceof ApiError ? e.message : 'Could not withdraw it.'),
      },
    );
  };

  // The API sends labels (T-0285); before it, the filter and the tags are not shown.
  const labelsOn = said.some((e) => e.label !== undefined);
  const bySource = said.filter((e) => !source || e.source_key === source);
  const counting = (items: ReadonlyArray<QuotedEvidence>) =>
    items.filter((e) => e.status === 'confirmed').length;
  const labelled = (l: EvidenceLabel | null) =>
    bySource.filter((e) => e.status === 'confirmed' && (e.label ?? null) === l).length;
  const unlabelled = labelled(null);
  const filter: LabelFilter = labelsOn ? labelFilter : 'all';

  // Labelling earlier evidence: one run across the engagement. A quote a
  // person cleared stays as they left it, so it is not one AI would label.
  const qc = useQueryClient();
  const labellingState = useEvidenceLabellingQuery(engagementId, labelsOn).data;
  const running = labellingState?.active_run != null;
  const lastRun = labellingState?.last_run ?? null;
  const aiCanLabel = said.some(
    (e) => e.status === 'confirmed' && !e.label && e.label_set_by !== 'person',
  );
  // The run writes every label when it ends: read Discovery again then.
  const wasRunning = React.useRef(false);
  React.useEffect(() => {
    if (running) {
      wasRunning.current = true;
      return;
    }
    if (!wasRunning.current) return;
    wasRunning.current = false;
    void qc.invalidateQueries({ queryKey: discoveryKeys.all(engagementId) });
    if (lastRun?.status === 'failed') {
      toast({
        message: lastRun.error_message ?? 'The labelling did not finish. No label was kept.',
        tone: 'critical',
      });
    }
  }, [running, lastRun, qc, engagementId, toast]);
  const shown = bySource.filter((e) =>
    filter === 'all' ? true : filter === 'none' ? !e.label : e.label === filter,
  );

  const changeLabel = (e: QuotedEvidence, label: EvidenceLabel | null) =>
    setLabel.mutate(
      { id: e.evidence_id, label },
      {
        onError: (err) =>
          toast({
            message: err instanceof ApiError ? err.message : 'Could not change the label.',
            tone: 'critical',
          }),
      },
    );
  const startLabelling = () =>
    labelling.mutate(undefined, {
      onSuccess: () =>
        toast({
          message:
            'AI is labelling the confirmed quotes that have no label, across this engagement. The labels appear when it finishes; change any that is wrong.',
        }),
      onError: (err) =>
        toast({
          message: err instanceof ApiError ? err.message : 'Could not start the labelling.',
          tone: 'critical',
        }),
    });
  const groups = groupBySource(shown, sources);
  const names = new Map(sources.map((s) => [s.key, s.name]));
  const waitFrom = [
    ...waiting.interviews.map((i) => i.code),
    // Documents the evidence finder read (T-0257); an older API sends none.
    ...(waiting.documents ?? []).map((d) => d.title),
  ];

  return (
    <div className="max-w-[980px] px-5 pb-8">
      {waiting.count > 0 ? (
        <Callout
          tone="ai"
          icon={Zap}
          className="mt-4"
          actions={
            // One review queue across interviews and documents, filtered to this area.
            <WorkspaceButton onClick={() => onReview(`area:${areaId}`)}>
              Review {waiting.count === 1 ? 'it' : `all ${waiting.count}`}
            </WorkspaceButton>
          }
        >
          {plural(waiting.count, 'more item')}
          {waitFrom.length ? ` from ${waitFrom.join(' and ')}` : ''}{' '}
          {waiting.count === 1 ? 'is' : 'are'} waiting for review. They count once you confirm them.
        </Callout>
      ) : null}
      {labelsOn ? (
        <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-2">
          <FilterChips
            aria-label="What the quotes describe"
            value={filter}
            onChange={setLabelFilter}
            options={[
              { value: 'all' as LabelFilter, label: 'All', count: counting(bySource) },
              ...EVIDENCE_LABELS.map((l) => ({
                value: l as LabelFilter,
                label: EVIDENCE_LABEL_TEXT[l],
                count: labelled(l),
              })),
              ...(unlabelled > 0
                ? [{ value: 'none' as LabelFilter, label: 'Not labelled', count: unlabelled }]
                : []),
            ]}
          />
          {running ? (
            <span className="flex items-center gap-1.5 text-meta text-ink-500">
              <Spinner className="h-3 w-3" /> AI is labelling the quotes that have no label…
            </span>
          ) : labellingState?.can_start && aiCanLabel ? (
            <button
              type="button"
              className={linkishClass()}
              disabled={labelling.isPending}
              onClick={startLabelling}
              title="AI reads each confirmed quote that has no label, across this engagement, and says what it describes"
            >
              <Zap className="h-3 w-3" aria-hidden="true" /> Ask AI to label those
            </button>
          ) : null}
        </div>
      ) : null}
      <ListGroupLabel
        count={counting(shown)}
        action={
          source ? (
            <span className="text-meta font-normal text-ink-500">
              from {names.get(source)} ·{' '}
              <button type="button" className={linkishClass()} onClick={onShowEveryone}>
                show everyone
              </button>
            </span>
          ) : undefined
        }
      >
        What people said
      </ListGroupLabel>
      {groups.length ? (
        groups.map((g) => (
          <div key={g.key} className="mb-3">
            <p className="mb-1.5 flex items-center gap-2 text-meta font-semibold text-ink-700">
              <span aria-hidden="true" className="inline-flex">
                {g.kind === 'document' ? (
                  <DocBadge name={g.name} />
                ) : g.kind === 'person' ? (
                  <PersonAvatar id={g.id} name={g.name} size={20} />
                ) : null}
              </span>
              {g.name}
              <span className="font-normal text-ink-500">
                · {plural(counting(g.items), 'quote')}
                {g.items.length > counting(g.items)
                  ? `, ${g.items.length - counting(g.items)} withdrawn`
                  : ''}
              </span>
            </p>
            <div className="overflow-hidden rounded-card border border-ink-100 bg-paper-0">
              {g.items.map((e) => (
                <SaidRow
                  key={e.evidence_id}
                  item={e}
                  highlighted={highlight === e.evidence_id}
                  tags={
                    <>
                      {labelsOn && e.status !== 'withdrawn' ? (
                        <LabelMenu
                          item={e}
                          busy={setLabel.isPending}
                          onSet={(label) => changeLabel(e, label)}
                        />
                      ) : null}
                      <SupportTags support={support.get(e.evidence_id)} />
                    </>
                  }
                  withdrawing={withdrawing === e.evidence_id}
                  busy={withdraw.isPending}
                  error={withdrawing === e.evidence_id ? withdrawError : null}
                  onStartWithdraw={() => {
                    setWithdrawError(null);
                    setWithdrawing(e.evidence_id);
                  }}
                  onCancelWithdraw={() => setWithdrawing(null)}
                  onWithdraw={(reason) => doWithdraw(e.evidence_id, e.number, reason)}
                />
              ))}
            </div>
          </div>
        ))
      ) : (
        <p className="text-meta text-ink-500">Nothing confirmed yet.</p>
      )}
    </div>
  );
}
